import { Typography } from "@mui/material"
import { DataGrid, GridColDef } from "@mui/x-data-grid"
import { translate } from "app/language/service"
import { IForm, IFormMediaCreativeForecastRow } from "form/model"
import { getFilteredCreativeRows } from "form/tools/creative"
import {
  getMediaCreativeForecastRows,
  refreshMediaCreativeList,
  synchronizeNewRequiredMediaSelection
} from "form/tools/creative-forecast"
import { MediaType } from "media/components/MediaType"
import React from "react"
import { FlexBox } from "ui/components/FlexBox"
import { LinearLoader } from "ui/components/LinearLoader"
import { getDataGridTexts } from "ui/tools/list"
import { ColumnHeader } from "../../../../ui/components/ColumnHeader"
import { ColorizedText } from "../screens/ColorizedText"

interface ICreativeForecastBoxProps {
  form: IForm
}

export const CreativeForecastBox = React.memo(({ form }: ICreativeForecastBoxProps): JSX.Element => {
  React.useEffect(() => refreshMediaCreativeList(form), [])
  const isCreateForm = form.context.isCreation
  if (form.creatives.hasLoadingForecastError) {
    return (
      <Typography color="error" fontSize=".9rem">
        {translate("campaign.creative.required.loading.error")}
      </Typography>
    )
  } else if (form.creatives.isLoadingForecast) {
    return <LinearLoader text={translate("campaign.creative.required.loading")} />
  }
  const rows = getMediaCreativeForecastRows(form)
  if (rows.length === 0) {
    return (
      <Typography color="textSecondary" fontSize=".9rem">
        {translate("campaign.creative.required.empty")}
      </Typography>
    )
  }
  const filteredRows = getFilteredCreativeRows(form.creatives.filter, rows)
  const columns: Array<GridColDef<IFormMediaCreativeForecastRow>> = [
    {
      field: "mediaType",
      headerName: translate("label.type"),
      flex: 2,
      renderCell: params => <MediaType type={params.row.mediaType} />
    },
    {
      field: "width",
      headerName: translate("label.width"),
      flex: 3
    },
    {
      field: "height",
      headerName: translate("label.height"),
      flex: 3
    },
    {
      field: "impressionRatio",
      flex: 4,
      sortable: false,
      renderHeader: () => (
        <ColumnHeader
          primaryText={translate("label.impression.ratio.primary")}
          secondaryText={translate("label.impression.ratio.secondary")}
        />
      ),
      renderCell: params => (
        <ColorizedText
          text={params.row.impressionRatio}
          difference={params.row.impressionDiff}
          isLoading={form.creatives.isLoadingForecast}
          disabled={params.row.impression === 0}
          isCreationForm={isCreateForm}
        />
      )
    },
    {
      field: "impressionPercent",
      flex: 2,
      renderHeader: () => <ColumnHeader primaryText={translate("label.impression.percent")} />,
      renderCell: params => (
        <ColorizedText
          text={params.row.impressionPercent === -1 ? "-" : `${params.row.impressionPercent}%`}
          difference={params.row.impressionDiff}
          isLoading={form.creatives.isLoadingForecast}
          disabled={params.row.impression === 0}
          isCreationForm={isCreateForm}
        />
      )
    },
    {
      field: "screensRatio",
      flex: 4,
      sortable: false,
      renderHeader: () => (
        <ColumnHeader
          primaryText={translate("label.screen.ratio.primary")}
          secondaryText={translate("label.screen.ratio.secondary")}
        />
      ),
      renderCell: params => (
        <ColorizedText
          text={params.row.screensRatio}
          difference={params.row.screensDiff}
          isLoading={form.creatives.isLoadingForecast}
          disabled={params.row.screen === 0}
          isCreationForm={isCreateForm}
        />
      )
    },
    {
      field: "screenPercent",
      flex: 2,
      renderHeader: () => <ColumnHeader primaryText={translate("label.screen.percent")} />,
      renderCell: params => (
        <ColorizedText
          text={params.row.screenPercent === -1 ? "-" : `${params.row.screenPercent}%`}
          difference={params.row.screensDiff}
          isLoading={form.creatives.isLoadingForecast}
          disabled={params.row.screen === 0}
          isCreationForm={isCreateForm}
        />
      )
    }
  ]
  return (
    <FlexBox
      vertical
      ai="start"
      jc="start"
      gap
      fullWidth
      sx={{
        "height": "100%",
        "p": "1rem",
        "& > *": {
          width: "15rem"
        },
        "& .MuiDataGrid-root": {
          width: "100%",
          minHeight: "20rem"
        },
        "& .MuiDataGrid-selectedRowCount": {
          visibility: "hidden"
        },
        "& .MuiDataGrid-cell": {
          fontSize: ".8rem",
          fontWeight: 300
        },
        "& .MuiDataGrid-row": {
          cursor: "pointer"
        },
        "& .MuiDataGrid-row.Mui-selected": {
          backgroundColor: "inherit"
        },
        "& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus, & .MuiDataGrid-cell":
          {
            outline: "none !important"
          },
        "& .MuiCheckbox-root.Mui-disabled": {
          display: "none"
        }
      }}>
      <DataGrid
        rows={filteredRows}
        columns={columns}
        checkboxSelection
        getRowId={r => r.forecastId}
        loading={form.creatives.isSynchronizing}
        isRowSelectable={params =>
          !form.creatives.isSynchronizing &&
          (params.row.mediaType === "video" || typeof params.row.formatId === "number")
        }
        disableColumnFilter
        disableColumnMenu
        keepNonExistentRowsSelected
        selectionModel={form.creatives.selectedRequiredMediaCreativeIds}
        initialState={{
          sorting: { sortModel: [{ field: "impressionPercent", sort: "desc" }] }
        }}
        onSelectionModelChange={newSelectedIds => synchronizeNewRequiredMediaSelection(newSelectedIds as string[])}
        rowHeight={40}
        autoPageSize
        localeText={getDataGridTexts("campaign.creative.filter.empty")}
      />
    </FlexBox>
  )
})
